import { readEnv } from './runtime-env'

/**
 * Minimal Channel Talk (channel.io) web messenger — only used to let non-dealers ask the BOLTB team
 * for dealer access. Loads the official plugin script on demand (no npm dependency) and uses the same
 * channel as the mobile app (VITE_CHANNEL_TALK_PLUGIN_KEY = EXPO_PUBLIC_CHANNEL_TALK_PLUGIN_KEY).
 */

type ChannelIOFn = ((...args: unknown[]) => void) & {
  q?: unknown[][]
  c?: (args: unknown[]) => void
}

declare global {
  interface Window {
    ChannelIO?: ChannelIOFn
    ChannelIOInitialized?: boolean
  }
}

const PLUGIN_SCRIPT_URL = 'https://cdn.channel.io/plugin/ch-plugin-web.js'

function ensureChannelIO(): ChannelIOFn {
  if (window.ChannelIO) return window.ChannelIO
  const queue: unknown[][] = []
  const ch: ChannelIOFn = (...args: unknown[]) => {
    queue.push(args)
  }
  ch.q = queue
  window.ChannelIO = ch
  if (!window.ChannelIOInitialized) {
    window.ChannelIOInitialized = true
    const script = document.createElement('script')
    script.async = true
    script.src = PLUGIN_SCRIPT_URL
    document.head.appendChild(script)
  }
  return ch
}

let booted: Promise<boolean> | null = null

function boot(pluginKey: string): Promise<boolean> {
  booted ??= new Promise<boolean>((resolve) => {
    ensureChannelIO()(
      'boot',
      { pluginKey, language: 'ko', hideChannelButtonOnBoot: true },
      (error: unknown) => resolve(!error),
    )
    // The plugin never calls back when its script is blocked or fails to load
    setTimeout(() => resolve(false), 8000)
  }).then((ok) => {
    if (!ok) booted = null
    return ok
  })
  return booted
}

/** Opens the messenger. False when not configured or the plugin could not boot (show a fallback). */
export async function openChannelTalk(): Promise<boolean> {
  const pluginKey = readEnv('VITE_CHANNEL_TALK_PLUGIN_KEY')
  if (!pluginKey) return false
  if (!(await boot(pluginKey))) return false
  window.ChannelIO?.('showMessenger')
  return true
}
